// src/dashboard/pages/teacher/Palmares/components/AddPalmaresDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaCopy, FaMedal, FaSave, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  formatEventLabel,
  getEventTitle,
  getMemberDisplayName,
} from "../utils/palmaresDashboardUtils";

import VisibilitySwitch from "./VisibilitySwitch";

const initialForm = {
  adherent_palmares_id: "",
  event_palmares_id: "",
  date_palmares: "",
  titre_palmares: "",
  resultats_palmares: "",
  classement_palmares: "",
  observations_palmares: "",
  is_active: true,
};

export default function AddPalmaresDrawer({
  isOpen,
  onClose,
  onSubmit,
  saving,
  adherents = [],
  events = [],
}) {
  const [formData, setFormData] = useState(initialForm);
  const [eventQuery, setEventQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);
    setEventQuery("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const filteredEvents = useMemo(() => {
    const query = eventQuery.trim().toLowerCase();

    if (!query) return events;

    return events.filter((event) =>
      formatEventLabel(event).toLowerCase().includes(query)
    );
  }, [events, eventQuery]);

  const selectedEvent = useMemo(() => {
    return events.find(
      (event) => String(event.id) === String(formData.event_palmares_id)
    );
  }, [events, formData.event_palmares_id]);

  const canSubmit = useMemo(() => {
    return (
      formData.adherent_palmares_id &&
      formData.date_palmares &&
      String(formData.titre_palmares || "").trim() &&
      !saving
    );
  }, [formData, saving]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCopyEventTitle = () => {
    if (!selectedEvent) {
      toast.info("Sélectionnez un événement d’abord.");
      return;
    }

    const title = getEventTitle(selectedEvent);

    if (!title) {
      toast.info("Cet événement n’a pas de titre exploitable.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      titre_palmares: prev.titre_palmares?.trim()
        ? prev.titre_palmares
        : title,
    }));

    if (!formData.titre_palmares?.trim()) {
      toast.success("Titre renseigné depuis l’événement.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouveau résultat
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter un palmarès
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sélectionnez l’adhérent, ajoutez la compétition ou l’événement,
              puis renseignez le résultat obtenu.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[#013E8E] shadow-sm dark:bg-slate-900 dark:text-indigo-300">
                <FaMedal />
              </span>

              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Informations principales
                </p>
                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  L’adhérent, la date et le titre sont obligatoires.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Adhérent
              </label>

              <select
                value={formData.adherent_palmares_id}
                onChange={(event) =>
                  updateField("adherent_palmares_id", event.target.value)
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
                autoFocus
              >
                <option value="">— Sélectionner un adhérent —</option>
                {adherents.map((adherent) => (
                  <option key={adherent.id} value={adherent.id}>
                    {getMemberDisplayName(adherent)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Date
              </label>

              <input
                type="date"
                value={formData.date_palmares}
                onChange={(event) =>
                  updateField("date_palmares", event.target.value)
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                required
              />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Événement lié
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={eventQuery}
                onChange={(event) => setEventQuery(event.target.value)}
                placeholder="Rechercher par nom, date ou lieu..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <select
                value={formData.event_palmares_id}
                onChange={(event) =>
                  updateField("event_palmares_id", event.target.value)
                }
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="">— Aucun événement —</option>
                {filteredEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {formatEventLabel(event)}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleCopyEventTitle}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaCopy />
                Copier le titre
              </button>
            </div>

            {formData.event_palmares_id ? (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                Événement sélectionné
                <button
                  type="button"
                  onClick={() => updateField("event_palmares_id", "")}
                  className="underline"
                >
                  Retirer
                </button>
              </div>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Titre du palmarès
            </label>

            <input
              type="text"
              value={formData.titre_palmares}
              onChange={(event) =>
                updateField("titre_palmares", event.target.value)
              }
              placeholder="Ex : 1er au Tournoi Régional"
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Résultat
              </label>

              <input
                type="text"
                value={formData.resultats_palmares}
                onChange={(event) =>
                  updateField("resultats_palmares", event.target.value)
                }
                placeholder="Ex : 3 combats gagnés"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Classement
              </label>

              <input
                type="text"
                value={formData.classement_palmares}
                onChange={(event) =>
                  updateField("classement_palmares", event.target.value)
                }
                placeholder="Ex : 2e, champion, qualifié..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Observations
            </label>

            <textarea
              value={formData.observations_palmares}
              onChange={(event) =>
                updateField("observations_palmares", event.target.value)
              }
              rows={5}
              placeholder="Notes, contexte, remarques..."
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>

          <VisibilitySwitch
            checked={!!formData.is_active}
            onChange={() => updateField("is_active", !formData.is_active)}
            label="Afficher ce palmarès sur la page d’accueil"
          />
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}